import Image from "next/image";
import Link from "@/components/Link";
import { AppRoutes } from "@/lib/routeResolver";
import { slugifyPageUrl, truncate } from "@/lib/helpers";
import styles from "./package-card.module.css";

// ─── Types ───────────────────────────────────────────────────────────

export interface ExperienceTourV2 {
  id: string;
  name: string;
  description: string;
  highlight?: string;
  region: {
    regionId: string;
    regionName: string;
    destinationId: string;
    destinationName: string;
    cityId: string;
    cityName: string;
  };
  photos: { url?: string; singleURL?: string; title?: string } | null;
  photoGallery: { url?: string; title?: string }[];
  tourTypeNames: string[];
  pageUrl: string;
  isWebsite: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────

const FALLBACK_IMGS = [
  "/main/img/tour/tour1.jpg",
  "/main/img/tour/tour2.jpg",
  "/main/img/tour/tour3.jpg",
  "/main/img/tour/tour4.jpg",
  "/main/img/tour/tour6.jpg",
];

function getTourPhoto(tour: ExperienceTourV2, index: number): string {
  return (
    tour.photos?.singleURL ||
    tour.photos?.url ||
    tour.photoGallery?.[0]?.url ||
    FALLBACK_IMGS[index % FALLBACK_IMGS.length]
  );
}

// ─── Component ───────────────────────────────────────────────────────

export default function ExperienceCardV2({
  tour,
  index,
}: {
  tour: ExperienceTourV2;
  index: number;
}) {
  const imgSrc = getTourPhoto(tour, index);
  const isExternal = imgSrc.startsWith("http");
  const description = truncate(tour.description, 100);
  const tourHref = AppRoutes.experienceTour(slugifyPageUrl(tour.pageUrl));
  const destHref = AppRoutes.experienceDestination(
    tour.region?.destinationName?.replace(/\s+/g, "-") ?? "",
  );

  return (
    <div className="tour-box th-ani gsap-cursor h-100">
      <div className="tour-box_img global-img">
        {tour.region?.destinationName && (
          <div className="tagcloud">
            <Link href={destHref} title={tour.region.destinationName}>
              {tour.region.destinationName}
            </Link>
          </div>
        )}
        <Link
          href={tourHref}
          title={tour.name}
          style={{ display: "block", position: "relative", height: "250px" }}
        >
          <Image
            src={imgSrc}
            alt={tour.name}
            fill
            style={{ objectFit: "cover" }}
            unoptimized={isExternal}
          />
        </Link>
      </div>
      <div className="tour-content">
        <h3
          className="box-title fw-bolder"
          style={{ textTransform: "capitalize", fontSize: "1.15rem" }}
        >
          <Link href={tourHref} title={tour.name}>{tour.name.toLowerCase()}</Link>
        </h3>
        {description && (
          <p
            className="tour-text text-start mb-2"
            style={{
              fontSize: "14px",
              color: "#666",
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {description}
          </p>
        )}
        {tour.tourTypeNames?.length > 0 && (
          <div className="tagcloud mt-2 mb-2">
            {tour.tourTypeNames.slice(0, 2).map((type, j) => (
              <span
                key={j}
                style={{
                  display: "inline-block",
                  fontSize: "11px",
                  padding: "2px 10px",
                  borderRadius: "3px",
                  background: "#f5f5f5",
                  color: "#555",
                  marginRight: "5px",
                  textTransform: "capitalize",
                }}
              >
                {type.toLowerCase()}
              </span>
            ))}
          </div>
        )}
        <div className={`tour-action ${styles.actions}`}>
          <span className={styles.duration}>
            <i className="fa-light fa-location-dot"></i>
            {tour.region?.cityName || tour.region?.destinationName}
          </span>
          <Link
            href={tourHref}
            className="th-btn style1 th-icon"
            title={`Explore ${tour.name}`}
          >
            Explore
            <span className="visually-hidden">{` ${tour.name}`}</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
